import type { Manifest } from '../api/authoring'
import type { FieldSpec } from './definition'
import { fieldsSchema, typeOf } from './manifest'
import { isDrawable, namesTheTree, orderedProperties, resolve, type Property, type Schema } from './schema'

// A tenant's pattern declaration, as this client reads and writes one.
//
// **The same file `model/definition.ts` is, for the second meta-type.**
// A declaration is the opaque fields of an ordinary document, so the
// contract describes it as raw JSON and the manifest carries its schema;
// this is where that JSON gets a shape, and where the handful of rules
// the *platform* states about a declaration get a second reading that a
// control can consult before it sends something the fold would refuse.
//
// Nothing here re-implements validation. `PatternType.Validate` and the
// compiler are the authority, and both run on every revision; what a
// control needs is narrower and different in kind — which scopes exist,
// which fields each of them offers, where a node sits in the tree. Those
// are questions about what to *offer*, and a form that offered a scope
// the declaration does not read would be asking an author for a refusal.

/** One typed record a pattern reads: its input, or one named reference. */
export interface InputSpec {
  name: string
  accepts: string
}

/**
 * One node of a pattern's tree.
 *
 * `linkId` is a plain string and not a link id, which is the platform's
 * own distinction rather than a looseness here: it addresses a node
 * inside an *expansion that does not exist yet*, so nothing in the
 * document it is declared on could ever resolve it.
 */
export interface PatternNode {
  linkId: string
  type: string
  slot?: string
  fields?: Record<string, unknown>
  bindings?: Record<string, string>
  /**
   * A relation of a record this pattern reads, as `$scope.relation`: the
   * node is that existing component, placed and composed in full, rather
   * than a node the expansion builds. It carries no bindings, no stated
   * fields and no children of its own, because it is its target.
   */
  place?: string
  children?: PatternNode[]
}

/** A whole declaration, as a `ComponentPattern` document holds it. */
export interface PatternDeclaration {
  patternName: string
  input: InputSpec
  params: FieldSpec[]
  refers: InputSpec[]
  tree: PatternNode
}

/**
 * The name a placement's own properties are read under.
 *
 * Reserved by the platform rather than declarable, and stated once here
 * for the same reason `PatternEditor` states it once: it is contract
 * vocabulary — the scope half of `$props.actionLabel` — and two spellings
 * of it in one client is one of them being wrong later.
 */
export const PROPS_SCOPE = 'props'

/**
 * Read a declaration out of a stored document's opaque fields.
 *
 * Everything absent becomes an empty value rather than undefined, because
 * every control below edits a list or an object and none of them should
 * have to ask whether the member was there. A declaration written before
 * `params` and `refers` were writable simply reads as having none.
 */
export function declarationOf(fields: Record<string, unknown>): PatternDeclaration {
  const input = isRecord(fields.input) ? fields.input : {}
  return {
    patternName: typeof fields.patternName === 'string' ? fields.patternName : '',
    input: {
      name: typeof input.name === 'string' ? input.name : '',
      accepts: typeof input.accepts === 'string' ? input.accepts : '',
    },
    params: Array.isArray(fields.params) ? (fields.params as FieldSpec[]) : [],
    refers: Array.isArray(fields.refers) ? (fields.refers as InputSpec[]) : [],
    tree: nodeOf(fields.tree),
  }
}

function nodeOf(value: unknown): PatternNode {
  if (!isRecord(value)) return { linkId: '', type: '' }
  return {
    linkId: typeof value.linkId === 'string' ? value.linkId : '',
    type: typeof value.type === 'string' ? value.type : '',
    slot: typeof value.slot === 'string' && value.slot !== '' ? value.slot : undefined,
    fields: isRecord(value.fields) ? value.fields : undefined,
    bindings: isRecord(value.bindings) ? (value.bindings as Record<string, string>) : undefined,
    // Read like every other member: a reader that dropped it would save a
    // placed node back as an empty one the next time anything is edited.
    place: typeof value.place === 'string' && value.place !== '' ? value.place : undefined,
    children: Array.isArray(value.children) ? value.children.map(nodeOf) : undefined,
  }
}

/**
 * One name a binding may read, and the contract behind it.
 *
 * `PatternType.Scopes()` answers the first half server-side, and this is
 * the same list assembled from the same three sources: the input, each
 * named reference, and `props`. The second half is what makes a binding
 * editor a *choice* rather than a text box — `props` is typed by the
 * declaration's own properties, and every other scope by the fields of
 * the record type it accepts.
 */
export interface Scope {
  name: string
  /** The record type it accepts, or undefined for the placement's own properties. */
  accepts?: string
  /** What a binding out of it may name. */
  fields: string[]
}

/**
 * Every scope this declaration's bindings may read, sorted as the
 * platform sorts them.
 *
 * The fields come off the manifest for a record and off the declaration
 * being edited for `props`, which is what lets the properties section and
 * the tree section stay in step inside one unsaved edit: a property added
 * above is bindable below before either has been sent.
 */
export function scopesOf(
  manifest: Manifest | undefined,
  declaration: PatternDeclaration,
): Scope[] {
  const scopes: Scope[] = [
    { name: PROPS_SCOPE, fields: declaration.params.map((param) => param.name).filter(Boolean) },
  ]
  for (const record of [declaration.input, ...declaration.refers]) {
    if (!record.name) continue
    scopes.push({ name: record.name, accepts: record.accepts, fields: recordFields(manifest, record.accepts) })
  }
  return scopes.sort((left, right) => left.name.localeCompare(right.name))
}

/** What a record type declares, by name, in declaration order. */
function recordFields(manifest: Manifest | undefined, typeName: string): string[] {
  const schema = fieldsSchema(manifest, typeName)
  if (!schema) return []
  return orderedProperties(schema, schema).map((property) => property.name)
}

/** One parsed `$scope.field`, or nothing for anything else. */
export function parseBinding(expression: string): { scope: string; field: string } | undefined {
  const match = /^\$([a-z][a-zA-Z0-9]*)\.([a-z][a-zA-Z0-9]*)$/.exec(expression)
  return match ? { scope: match[1], field: match[2] } : undefined
}

/** The one binding form this version of the platform accepts. */
export function bindingOf(scope: string, field: string): string {
  return `$${scope}.${field}`
}

/**
 * The types a record may be — what an input or a named reference accepts.
 *
 * **The role, asked rather than known.** A pattern binds values out of
 * durable content, and durable content is exactly what the record role
 * means; the compiler refuses an input accepting a layout in those words.
 * So the picker offers the tenant's records and a designer never has to
 * hold a list of which types those are.
 */
export function recordTypes(manifest: Manifest | undefined): string[] {
  return (manifest?.types ?? [])
    .filter((entry) => entry.role === 'record')
    .map((entry) => entry.name)
    .sort()
}

/**
 * The types a pattern's tree may emit at its root.
 *
 * Everything the manifest publishes, less the two meta-types: a
 * declaration is not something a pattern produces, and both say so about
 * themselves. Below the root the question is narrower and better asked —
 * the parent's own containment answers it, which is the same list a
 * palette offers and the same rule the compiler checks.
 */
export function emittableTypes(manifest: Manifest | undefined): string[] {
  return (manifest?.types ?? [])
    .filter((entry) => !entry.defines && !entry.declaresPatterns)
    .map((entry) => entry.name)
    .sort()
}

/**
 * The places on one emitted type a node may **bind** into, named as the
 * key a binding is written under: `title`, or `seo.description` for one
 * member of an object.
 *
 * Every declared value, drawable or not, and the difference matters. A
 * binding names a whole value and the compiler checks it for
 * assignability; whether this client could draw a *control* for that
 * value is a question about a form and has nothing to do with it. A
 * practice's seven tags are a list of rich text — undrawable, and exactly
 * the binding the seeded card makes.
 *
 * **An object is offered as its members, never whole.** Every source a
 * binding reads is a record field or a property, and neither is ever an
 * object, so `seo` as a whole is a row that could only be refused. Its
 * description and keywords are what an author means. The same walk the
 * compiler's `destinationOf` makes leaves out what it refuses:
 *
 * - an **arrangement** — anything holding a link id, a Page's `slots` or
 *   a Container's `items` — because the expander writes it from the tree,
 *   and a bound list of tags would name children that do not exist;
 * - a **discriminated object** — a background whose `palette` applies
 *   only when its `kind` says so — because a bound member cannot say
 *   which case the object is in.
 */
export function bindableProperties(manifest: Manifest | undefined, typeName: string): Property[] {
  const schema = fieldsSchema(manifest, typeName)
  if (!schema) return []
  return destinations(schema, schema, '')
}

function destinations(object: Schema, root: Schema, prefix: string): Property[] {
  const found: Property[] = []
  for (const property of orderedProperties(object, root)) {
    if (namesTheTree(property.schema, root)) continue
    const resolved = resolve(property.schema, root)
    const name = `${prefix}${property.name}`
    if (resolved?.type === 'object') {
      if (!discriminated(resolved)) found.push(...destinations(resolved, root, `${name}.`))
      continue
    }
    found.push({ ...property, name })
  }
  return found
}

function discriminated(object: Schema): boolean {
  return Object.values(object.properties ?? {}).some((member) => member['x-when'] !== undefined)
}

/**
 * Whether a binding's key names something the emitted type declares at
 * all, offered or not.
 *
 * Asked separately from `bindableProperties` because the table draws what
 * a node already holds, and holding a key the designer would no longer
 * offer — a whole `seo`, a stated `items` — is not the same fault as
 * naming a field the type does not have. Only the second is a warning
 * about the type.
 */
export function declaresPath(manifest: Manifest | undefined, typeName: string, key: string): boolean {
  const schema = fieldsSchema(manifest, typeName)
  let current: Schema | undefined = schema
  for (const member of key.split('.')) {
    current = schema && current ? resolve(current.properties?.[member], schema) : undefined
    if (!current) return false
  }
  return true
}

/**
 * The field a binding's key writes into: `seo` for `seo.description`.
 *
 * One field takes one source, so a key and every path below it compete
 * for it — a stated `seo` and a bound `seo.description` are refused
 * together, and a form must not offer the second beside the first.
 */
export function fieldOf(key: string): string {
  const dot = key.indexOf('.')
  return dot < 0 ? key : key.slice(0, dot)
}

/**
 * The fields of one emitted type a node may **state** as a literal.
 *
 * Drawable ones only, which is the same gate every generated form
 * applies: a member this client cannot draw would be a heading with no
 * control under it. A stored literal on such a member rides through
 * untouched, because the tree is edited as a shape and carried whole.
 */
export function statableProperties(manifest: Manifest | undefined, typeName: string): Property[] {
  const schema = fieldsSchema(manifest, typeName)
  if (!schema) return []
  return orderedProperties(schema, schema).filter((property) => isDrawable(property.schema, schema))
}

/** The schema a node's stated fields are drawn from, for `SchemaForm`. */
export function emittedSchema(manifest: Manifest | undefined, typeName: string): Schema | undefined {
  return fieldsSchema(manifest, typeName)
}

/** Whether the manifest declares this type at all. */
export function declared(manifest: Manifest | undefined, typeName: string): boolean {
  return Boolean(typeOf(manifest, typeName))
}

/**
 * A node's address inside the tree: the child index at each level, from
 * the root down. The root is the empty path.
 *
 * Indexes rather than link ids, because a tree being edited legitimately
 * holds a duplicate or an empty one — a node added a moment ago has no
 * name yet — and an address that stopped resolving while the author was
 * typing would be an address the editor could not use.
 */
export type NodePath = number[]

export function nodeAt(tree: PatternNode, path: NodePath): PatternNode | undefined {
  let node: PatternNode | undefined = tree
  for (const index of path) {
    node = node?.children?.[index]
  }
  return node
}

/** The tree with one node replaced, and every other node untouched. */
export function withNode(tree: PatternNode, path: NodePath, next: PatternNode): PatternNode {
  if (path.length === 0) return next
  const [index, ...rest] = path
  const children = tree.children ?? []
  const child = children[index]
  if (!child) return tree
  return {
    ...tree,
    children: children.map((entry, at) => (at === index ? withNode(child, rest, next) : entry)),
  }
}

/** The tree with one node's children replaced. */
export function withChildren(tree: PatternNode, path: NodePath, children: PatternNode[]): PatternNode {
  const node = nodeAt(tree, path)
  if (!node) return tree
  return withNode(tree, path, { ...node, children: children.length > 0 ? children : undefined })
}

/**
 * Every link id in the tree, in walk order.
 *
 * They are unique across the **whole** expansion rather than among
 * siblings — the platform's rule, and stricter than a placement's —
 * because an expanded tree is addressed by link-id path. A designer that
 * reported a duplicate only on save would let an author build a whole
 * card before saying so.
 */
export function linkIds(tree: PatternNode): string[] {
  const names: string[] = []
  const walk = (node: PatternNode) => {
    names.push(node.linkId)
    for (const child of node.children ?? []) walk(child)
  }
  walk(tree)
  return names
}

/**
 * A link id nothing in the tree is using yet, derived from the type it
 * names.
 *
 * A starting point rather than a decision: the author renames it, and the
 * name they choose is what addresses the node in every expansion. Derived
 * from the type because "heading2" is a better first guess than "node3"
 * and both are equally easy to replace.
 */
export function freeLinkId(tree: PatternNode, typeName: string): string {
  const taken = new Set(linkIds(tree))
  const local = typeName.slice(typeName.indexOf('/') + 1).replace(/[^A-Za-z0-9]/g, '')
  const stem = local ? local.charAt(0).toLowerCase() + local.slice(1) : 'node'
  if (!taken.has(stem)) return stem
  for (let suffix = 2; ; suffix += 1) {
    if (!taken.has(`${stem}${suffix}`)) return `${stem}${suffix}`
  }
}

/**
 * A node's stated fields, reduced to what the author actually stated.
 *
 * An untouched control and an absent member are one value to the fold, so
 * an emptied box goes unsent rather than being written as `""`. A `false`
 * is kept, unlike a creation body's: a literal a pattern fixes for every
 * expansion is a decision the declaration makes, and "off" is one of the
 * two things it can decide.
 */
export function statedFields(value: Record<string, unknown>): Record<string, unknown> | undefined {
  const stated: Record<string, unknown> = {}
  for (const [name, member] of Object.entries(value)) {
    if (member === undefined || member === '' || member === null) continue
    stated[name] = member
  }
  return Object.keys(stated).length > 0 ? stated : undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The relations a node of this type could be placed from: every single
 * relation of a record this pattern reads whose accepted types include the
 * node's own. Spelt `$scope.relation`, the form `place` takes.
 */
export function placeableFrom(manifest: Manifest | undefined, scopes: Scope[], typeName: string): string[] {
  const options: string[] = []
  for (const scope of scopes) {
    if (!scope.accepts) continue
    for (const reference of typeOf(manifest, scope.accepts)?.references ?? []) {
      if (reference.multiple || reference.authoredNames) continue
      if (reference.accepts.includes(typeName)) options.push(bindingOf(scope.name, reference.name))
    }
  }
  return options
}
